import React, { useState, useRef, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiErr, API } from "@/lib/api";
import { rupiah, fmtDate } from "@/lib/format";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import NumberInput from "@/components/ui/number-input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Upload, Trash2, FileCheck2, CheckCircle2, Receipt, Paperclip } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

export default function PaymentRecorder({ leadId, readOnly, initialKind = "design", onClose }) {
  const qc = useQueryClient();
  const fileRef = useRef();
  const invoiceFileRef = useRef();
  // Design and Production carry fully independent payment ledgers — this toggle switches
  // which one the form below reads/writes, without needing two separate sheets. Opens
  // pre-set to whichever tab (Design/Produksi) the Piutang page was showing.
  const [kind, setKind] = useState(initialKind); // "design" | "produksi"
  useEffect(() => { if (leadId) setKind(initialKind); }, [leadId, initialKind]);
  const isProd = kind === "produksi";
  const [terminLabel, setTerminLabel] = useState("Umum");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(today());
  const [note, setNote] = useState("");
  const [proof, setProof] = useState(null); // {path, name}
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [invoiceMode, setInvoiceMode] = useState("upload"); // "upload" | "existing"
  const [invoiceFile, setInvoiceFile] = useState(null); // {path, name}
  const [invoiceUploading, setInvoiceUploading] = useState(false);
  const [invoiceDocId, setInvoiceDocId] = useState("");

  const { data: lead, isLoading } = useQuery({
    queryKey: ["lead", leadId], enabled: !!leadId,
    queryFn: async () => (await api.get(`/leads/${leadId}`)).data,
  });
  const { data: docs = [] } = useQuery({
    queryKey: ["documents", leadId], enabled: !!leadId,
    queryFn: async () => (await api.get(`/documents?lead_id=${leadId}`)).data,
  });
  const invoiceDocs = docs.filter((d) => d.type === "invoice");

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["lead", leadId] });
    qc.invalidateQueries({ queryKey: ["receivables"] });
    qc.invalidateQueries({ queryKey: ["leads"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProof({ path: data.path, name: data.name });
      toast.success("Bukti transfer terunggah");
    } catch (err) { toast.error(apiErr(err)); } finally { setUploading(false); }
  };

  const uploadInvoice = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setInvoiceUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setInvoiceFile({ path: data.path, name: data.name });
      toast.success("Invoice terunggah");
    } catch (err) { toast.error(apiErr(err)); } finally { setInvoiceUploading(false); }
  };

  const submit = async () => {
    if (!Number(amount) || Number(amount) <= 0) { toast.error("Nominal pembayaran harus lebih dari 0"); return; }
    if (!proof) { toast.error("Upload bukti transfer dulu agar pembayaran valid"); return; }
    if (invoiceMode === "upload" && !invoiceFile) { toast.error("Upload file invoice dulu, atau pilih dokumen invoice yang sudah dibuat"); return; }
    if (invoiceMode === "existing" && !invoiceDocId) { toast.error("Pilih dokumen invoice untuk lead ini, atau upload file invoice"); return; }
    setSaving(true);
    try {
      await api.post(`/leads/${leadId}/${isProd ? "produksi-payments" : "payments"}`, {
        termin_label: terminLabel === "Umum" ? "" : terminLabel,
        amount: Number(amount), date, proof_path: proof.path, proof_name: proof.name, note,
        invoice_path: invoiceMode === "upload" ? invoiceFile.path : "",
        invoice_name: invoiceMode === "upload" ? invoiceFile.name : "",
        invoice_doc_id: invoiceMode === "existing" ? invoiceDocId : null,
      });
      toast.success("Pembayaran dicatat");
      setAmount(""); setNote(""); setProof(null); if (fileRef.current) fileRef.current.value = "";
      setInvoiceFile(null); setInvoiceDocId(""); if (invoiceFileRef.current) invoiceFileRef.current.value = "";
      refresh();
    } catch (err) { toast.error(apiErr(err)); } finally { setSaving(false); }
  };

  const delPay = async (pid) => {
    if (!window.confirm("Hapus catatan pembayaran ini?")) return;
    try { await api.delete(`/leads/${leadId}/${isProd ? "produksi-payments" : "payments"}/${pid}`); toast.success("Pembayaran dihapus"); refresh(); }
    catch (err) { toast.error(apiErr(err)); }
  };

  const token = localStorage.getItem("crm_token");
  const proofUrl = (p) => `${API}/files/${p}?auth=${token}`;
  const ts = (isProd ? lead?.produksi_termins_status : lead?.termins_status) || [];
  const waPhone = lead?.no_hp ? lead.no_hp.replace(/[^0-9]/g, "").replace(/^0/, "62") : null;
  const waReminder = (t) => {
    if (!waPhone) return;
    const due = t.due_date ? fmtDate(t.due_date) : null;
    const text = `Halo ${lead.nama}, mengingatkan untuk pembayaran termin "${t.label}" sebesar ${rupiah(t.sisa)}` +
      (due ? ` yang jatuh tempo pada ${due}.` : ".") + ` Terima kasih.`;
    window.open(`https://wa.me/${waPhone}?text=${encodeURIComponent(text)}`, "_blank");
  };
  const history = (isProd ? lead?.produksi_payment_history : lead?.payment_history) || [];
  const terminOptions = ["Umum", ...ts.map((t) => t.label).filter(Boolean)];
  const hasProduksi = (lead?.nilai_produksi || 0) > 0;

  return (
    <Sheet open={!!leadId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="bg-[#0f0f12] border-white/10 w-full sm:max-w-md overflow-y-auto hide-scrollbar p-0">
        {isLoading || !lead ? (
          <div><SheetTitle className="sr-only">Memuat</SheetTitle><SheetDescription className="sr-only">Memuat data pembayaran</SheetDescription></div>
        ) : (
          <div className="p-5" data-testid="payment-recorder">
            <SheetHeader className="mb-3">
              <div className="flex items-center justify-between pr-8 gap-2">
                <SheetTitle className="font-heading text-xl truncate">{lead.nama}</SheetTitle>
                {(isProd ? lead.produksi_lunas : lead.lunas) ? <span data-testid="badge-lunas" className="text-xs px-2 py-0.5 rounded border bg-emerald-500/15 text-emerald-400 border-emerald-500/30 inline-flex items-center gap-1"><CheckCircle2 size={12} />Lunas</span>
                  : <span className="text-xs px-2 py-0.5 rounded border bg-amber-500/15 text-amber-400 border-amber-500/30">Belum Lunas</span>}
              </div>
              <SheetDescription className="text-zinc-500">{lead.layanan || "-"} · closing {fmtDate(lead.closing_date)}</SheetDescription>
            </SheetHeader>

            {hasProduksi && (
              <div className="flex bg-[#18181B] rounded-lg p-1 border border-white/5 mb-3">
                <button data-testid="pay-kind-design" onClick={() => { setKind("design"); setTerminLabel("Umum"); }} className={`flex-1 py-1.5 rounded-md text-xs font-medium ${!isProd ? "bg-blue-500/20 text-blue-300" : "text-zinc-500"}`}>Design</button>
                <button data-testid="pay-kind-produksi" onClick={() => { setKind("produksi"); setTerminLabel("Umum"); }} className={`flex-1 py-1.5 rounded-md text-xs font-medium ${isProd ? "bg-blue-500/20 text-blue-300" : "text-zinc-500"}`}>Produksi</button>
              </div>
            )}

            <div className="grid grid-cols-3 gap-2 text-center mb-4">
              <div className="p-3 rounded-xl bg-[#18181B]"><div className="text-xs text-zinc-500">Nilai</div><div className="font-semibold text-blue-300 text-sm tabular-nums">{rupiah(isProd ? lead.nilai_produksi : lead.nilai_deal)}</div></div>
              <div className="p-3 rounded-xl bg-[#18181B]"><div className="text-xs text-zinc-500">Dibayar</div><div className="font-semibold text-emerald-400 text-sm tabular-nums">{rupiah(isProd ? lead.produksi_paid_total : lead.paid_total)}</div></div>
              <div className="p-3 rounded-xl bg-[#18181B]"><div className="text-xs text-zinc-500">Sisa</div><div className="font-semibold text-amber-400 text-sm tabular-nums">{rupiah(isProd ? lead.produksi_sisa_piutang : lead.sisa_piutang)}</div></div>
            </div>

            {ts.length > 0 ? (
              <div className="mb-4">
                <div className="text-[11px] uppercase tracking-wide text-zinc-500 mb-2">Status per Termin</div>
                <div className="space-y-1.5">
                  {ts.map((t, i) => (
                    <div key={i} className="flex justify-between items-center gap-2 text-xs bg-[#18181B] rounded-lg px-2.5 py-2" data-testid={`termin-status-${i}`}>
                      <span className="truncate">{t.label} <span className="text-zinc-600">· {t.pct}%</span>{t.due_date && <span className="text-zinc-600"> · jatuh tempo {fmtDate(t.due_date)}</span>}</span>
                      <span className="shrink-0 flex items-center gap-1.5">
                        {rupiah(t.paid)} / {rupiah(t.amount)} {t.sisa <= 0 ? <span className="text-emerald-400">✓</span> : <span className="text-amber-400">(sisa {rupiah(t.sisa)})</span>}
                        {t.sisa > 0 && waPhone && (
                          <button type="button" onClick={() => waReminder(t)} title="Kirim reminder WhatsApp ke klien" data-testid={`termin-wa-reminder-${i}`}
                            className="text-emerald-400 hover:bg-emerald-500/10 rounded-md p-1 shrink-0">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38c1.45.79 3.08 1.21 4.79 1.21 5.46 0 9.91-4.45 9.91-9.91S17.5 2 12.04 2zm0 18.15c-1.51 0-2.99-.41-4.28-1.18l-.31-.18-3.12.82.83-3.04-.2-.31a8.15 8.15 0 01-1.25-4.35c0-4.51 3.67-8.18 8.18-8.18 2.18 0 4.23.85 5.77 2.39a8.13 8.13 0 012.4 5.79c0 4.51-3.67 8.18-8.18 8.18z"/></svg>
                          </button>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="mb-4 text-[11px] text-zinc-500 bg-[#18181B] rounded-lg px-2.5 py-2">
                Belum ada skema termin untuk {isProd ? "Produksi" : "Design"} — atur dulu di detail lead, tab Bayar, sebelum status per termin bisa tampil di sini.
              </div>
            )}

            {!readOnly && (
              <div className="rounded-xl border border-white/10 p-3 mb-4">
                <div className="text-sm font-medium mb-2 flex items-center gap-1.5"><Receipt size={15} className="text-blue-400" />Catat Pembayaran Masuk</div>
                <div className="space-y-2">
                  <Select value={terminLabel} onValueChange={setTerminLabel}>
                    <SelectTrigger data-testid="pay-termin" className="bg-[#18181B] border-white/10 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#18181B] border-white/10">{terminOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                  <NumberInput data-testid="pay-amount" placeholder="Nominal dibayar (Rp)" value={amount} onChange={(e) => setAmount(e.target.value)} className="bg-[#18181B] border-white/10 h-9" />
                  <Input data-testid="pay-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="bg-[#18181B] border-white/10 h-9" />
                  <Input data-testid="pay-note" placeholder="Catatan (opsional)" value={note} onChange={(e) => setNote(e.target.value)} className="bg-[#18181B] border-white/10 h-9" />
                  <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={upload} className="hidden" data-testid="pay-proof-input" />
                  <Button data-testid="pay-proof-btn" type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="w-full h-9 bg-[#18181B] border-white/10">
                    {uploading ? <Loader2 size={14} className="animate-spin mr-1.5" /> : proof ? <FileCheck2 size={14} className="mr-1.5 text-emerald-400" /> : <Upload size={14} className="mr-1.5" />}
                    {proof ? `Bukti: ${proof.name}` : "Upload Bukti Transfer *"}
                  </Button>

                  <div className="pt-1 border-t border-white/5">
                    <div className="text-[11px] text-zinc-500 mb-1.5 mt-2">Invoice (wajib) *</div>
                    <div className="flex gap-1.5 mb-2">
                      <button type="button" data-testid="invoice-mode-upload" onClick={() => setInvoiceMode("upload")}
                        className={`flex-1 h-8 rounded-lg text-xs font-medium border ${invoiceMode === "upload" ? "bg-blue-500/20 text-blue-300 border-blue-500/40" : "bg-[#18181B] text-zinc-400 border-white/10"}`}>Upload File</button>
                      <button type="button" data-testid="invoice-mode-existing" onClick={() => setInvoiceMode("existing")}
                        className={`flex-1 h-8 rounded-lg text-xs font-medium border ${invoiceMode === "existing" ? "bg-blue-500/20 text-blue-300 border-blue-500/40" : "bg-[#18181B] text-zinc-400 border-white/10"}`}>Pilih Dokumen</button>
                    </div>
                    {invoiceMode === "upload" ? (
                      <>
                        <input ref={invoiceFileRef} type="file" accept="image/*,application/pdf" onChange={uploadInvoice} className="hidden" data-testid="invoice-file-input" />
                        <Button data-testid="invoice-upload-btn" type="button" variant="outline" onClick={() => invoiceFileRef.current?.click()} disabled={invoiceUploading} className="w-full h-9 bg-[#18181B] border-white/10">
                          {invoiceUploading ? <Loader2 size={14} className="animate-spin mr-1.5" /> : invoiceFile ? <FileCheck2 size={14} className="mr-1.5 text-emerald-400" /> : <Upload size={14} className="mr-1.5" />}
                          {invoiceFile ? `Invoice: ${invoiceFile.name}` : "Upload File Invoice *"}
                        </Button>
                      </>
                    ) : (
                      <Select value={invoiceDocId} onValueChange={setInvoiceDocId}>
                        <SelectTrigger data-testid="invoice-doc-select" className="bg-[#18181B] border-white/10 h-9">
                          <SelectValue placeholder={invoiceDocs.length ? "Pilih dokumen invoice…" : "Belum ada dokumen invoice — buat di tab Dokumen"} />
                        </SelectTrigger>
                        <SelectContent className="bg-[#18181B] border-white/10">
                          {invoiceDocs.map((d) => <SelectItem key={d.doc_id} value={d.doc_id}>{d.number} · {rupiah(d.total)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  <Button data-testid="pay-submit" onClick={submit} disabled={saving} className="w-full h-9 bg-emerald-600 hover:bg-emerald-700">{saving ? <Loader2 size={14} className="animate-spin" /> : "Catat Pembayaran"}</Button>
                </div>
              </div>
            )}

            <div className="text-[11px] uppercase tracking-wide text-zinc-500 mb-2">Riwayat Pembayaran ({history.length})</div>
            <div className="space-y-2">
              {history.slice().reverse().map((p) => (
                <div key={p.payment_id} className="bg-[#18181B] rounded-xl p-3" data-testid={`pay-history-${p.payment_id}`}>
                  <div className="flex justify-between items-start gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium">{p.termin_label || "Pembayaran"} · <span className="text-emerald-400 tabular-nums">{rupiah(p.amount)}</span></div>
                      <div className="text-[11px] text-zinc-500">{fmtDate(p.date)} · oleh {p.by}</div>
                      {p.note && <div className="text-[11px] text-zinc-400 mt-0.5">{p.note}</div>}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {p.proof_path && <a href={proofUrl(p.proof_path)} target="_blank" rel="noreferrer" title="Bukti transfer" className="text-cyan-300 p-1.5 hover:bg-white/5 rounded-lg" data-testid={`pay-proof-view-${p.payment_id}`}><Paperclip size={14} /></a>}
                      {p.invoice_path && <a href={proofUrl(p.invoice_path)} target="_blank" rel="noreferrer" title="Invoice" className="text-blue-300 p-1.5 hover:bg-white/5 rounded-lg" data-testid={`pay-invoice-view-${p.payment_id}`}><FileCheck2 size={14} /></a>}
                      {p.invoice_doc_id && <a href={`/cetak/${p.invoice_doc_id}`} target="_blank" rel="noreferrer" title="Lihat dokumen invoice" className="text-blue-300 p-1.5 hover:bg-white/5 rounded-lg" data-testid={`pay-invoice-doc-${p.payment_id}`}><FileCheck2 size={14} /></a>}
                      {!readOnly && <button data-testid={`pay-delete-${p.payment_id}`} onClick={() => delPay(p.payment_id)} className="text-red-400 p-1.5 hover:bg-red-500/10 rounded-lg"><Trash2 size={13} /></button>}
                    </div>
                  </div>
                </div>
              ))}
              {history.length === 0 && <p className="text-sm text-zinc-600 text-center py-4">Belum ada pembayaran tercatat</p>}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
